// @ts-nocheck
/**
 * Notification Button Component
 *
 * Header button that displays notification badge and opens notification panel.
 * Shows combined unread count from main and dashboard notification stores.
 */

import React, { useState, useCallback, useMemo, useRef } from 'react';
import { Bell } from 'lucide-react';
import { useNotificationStore, useUnreadCount } from '@shared/store/notificationStore';
import { useDashboardNotificationStore, useDashboardUnreadCount } from '@pages/dashboard/store/dashboardNotificationStore';
import { NotificationPanel } from './NotificationPanel';

export const NotificationButton: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const mainUnreadCount = useUnreadCount();
  const dashboardUnreadCount = useDashboardUnreadCount();
  const showBadge = useNotificationStore((state) => state.preferences.showBadge);

  // Combined unread count from both stores
  const unreadCount = useMemo(
    () => mainUnreadCount + dashboardUnreadCount,
    [mainUnreadCount, dashboardUnreadCount]
  );

  const handleToggle = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  return (
    <div className="relative">
      {/* Notification Button */}
      <button
        ref={buttonRef}
        onClick={handleToggle}
        className="
          app-header-icon-button notification-button
          relative min-h-[36px] min-w-[36px] p-1.5
          rounded-lg
          text-dark-muted hover:text-dark-text
          hover:bg-dark-surface/50
          transition-all duration-150
          focus:outline-none focus:ring-2 focus:ring-inset focus:ring-brand-500/50
        "
        aria-label="Notifications"
        aria-expanded={isOpen}
        data-interaction-id='button-notifications'>
        <Bell className="h-5 w-5" />

        {/* Unread Badge */}
        {showBadge && unreadCount > 0 && (
          <span
            className="
              absolute top-1 right-1
              flex items-center justify-center
              min-w-[18px] h-[18px] px-1
              bg-red-500 text-white
              text-[10px] font-bold
              rounded-full
              animate-pulse
            "
            aria-label={`${unreadCount} unread notifications`}
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>
      {/* Notification Panel Dropdown - Uses portal for proper positioning */}
      {isOpen && <NotificationPanel onClose={handleClose} buttonRef={buttonRef} />}
    </div>
  );
};

